// Teach once (U7), pure parts: what the scope chooser is about, its keys, the default handling
// for where it was offered, and its words. "Just this once" is the default: nothing changes
// without a choice, and a wider choice only files a proposal the owner applies on Learning.
import type { Verdict } from "@shared/measure";
import { DESTINATIONS, DESTINATION_LABEL, type Destination } from "@shared/routing";
import {
  defaultHandling,
  TEACH_SCOPES,
  type TeachOption,
  type TeachRequest,
  type TeachScope,
  type TeachSurface,
} from "@shared/teach";

export interface TeachTarget {
  threadKey: string;
  surface: TeachSurface;
  /** The card or subject the action was about (the chooser's heading). */
  title: string;
  cardId?: string;
  verdict?: Verdict;
}

/** A "Draft needed rewriting" verdict is about the words, not where mail goes: no chooser. */
export function offersTeach(surface: TeachSurface, verdict?: Verdict | null): boolean {
  return !(surface === "verdict" && verdict === "draft_rewrite");
}

export function initialHandling(target: TeachTarget): Destination {
  return defaultHandling(target.surface, target.verdict ?? null);
}

/** "1"–"4" pick a scope. */
export function scopeForKey(key: string): TeachScope | null {
  const i = Number(key) - 1;
  return Number.isInteger(i) ? (TEACH_SCOPES[i] ?? null) : null;
}

/** Letter keys for the handling: c cards, b brief only, r receipts, s screen out. */
export const HANDLING_KEY: Record<Destination, string> = {
  cards: "c",
  brief: "b",
  receipts: "r",
  screen_out: "s",
};

export function handlingForKey(key: string): Destination | null {
  const k = key.toLowerCase();
  return DESTINATIONS.find((d) => HANDLING_KEY[d] === k) ?? null;
}

export function canChoose(option: TeachOption | undefined): boolean {
  return !!option && option.unavailable === null;
}

export function teachRequest(
  target: TeachTarget,
  scope: Exclude<TeachScope, "once">,
  handling: Destination,
): TeachRequest {
  return {
    threadKey: target.threadKey,
    scope,
    handling,
    surface: target.surface,
    ...(target.cardId ? { cardId: target.cardId } : {}),
  };
}

export const SURFACE_HEADING: Record<TeachSurface, string> = {
  dismiss: "Dismissed. Just this once, or every time?",
  restore: "Brought back. Just this once, or every time?",
  verdict: "Noted. Just this once, or every time?",
};

export function filedMessage(label: string): string {
  return `Proposed on Learning: ${label}. Nothing changes until you apply it.`;
}

export function handlingWords(d: Destination): string {
  return DESTINATION_LABEL[d];
}

/**
 * Learning: what a teach proposal shows under its diff. The dry run tests the saved rule, so once
 * the owner edits the text it no longer describes what Apply would write: it gives way to a note.
 */
export function dryRunShown(
  proposal: { source: string; next: string },
  next: string,
): "dry_run" | "edited" | null {
  if (proposal.source !== "teach") return null;
  return next === proposal.next ? "dry_run" : "edited";
}

export const DRY_RUN_EDITED =
  "The look back over your mail covers Claude’s text, not your edit. Reset to Claude’s text to see it again.";
